<template>
  <v-row>
    <v-dialog v-model="showDialog" scrollable max-width="90%" max-height="90%">
      <v-skeleton-loader
        v-if="loading"
        type="article, actions"
      ></v-skeleton-loader>
      <v-card v-if="!loading">
        <v-card-title>
          <v-panel>
              <span style="font-size: 20px">
                {{ $t("wkt.admin.teams.team.edit_title") }}
              </span>
          </v-panel>
        </v-card-title>
        <v-card-text>
          <v-container fluid @wheel="onWheel">
            <v-form ref="form">
              <v-row style="margin-top: 5px" align="center">
                <v-col cols="4" class="d-flex align-center">
                  <div
                    :style="{
                      backgroundColor: convertColorToHex(data.color),
                      width: '22px',
                      height: '22px',
                      cursor: 'pointer',
                      border: '1px solid grey',
                      marginRight: '10px',
                    }"
                    @click="openColorDialog()"
                  />
                  <v-text-field
                    v-model="data.shortName"
                    :label="$t('wkt.admin.teams.team.short_name')"
                    autocomplete="none"
                    :rules="[validationRules.required]"
                    maxLength="20"
                    counter="20" />
                </v-col>
                <v-col cols="5">
                  <v-row align="center">
                    <v-col cols="8">
                      <v-text-field
                        v-model="data.internalCode"
                        :label="$t('wkt.admin.teams.team.internal-code')"
                        maxLength="20"
                        counter="20" />
                    </v-col>
                    <v-col cols="4" class="d-flex justify-end">
                      <v-switch
                        v-model="data.enabled"
                        :label="$t('wkt.admin.teams.team.enabled')" />
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="data.description"
                    :label="$t('wkt.admin.teams.team.description')"
                    autocomplete="none"
                    :rules="[validationRules.required]"
                    maxLength="250"
                    counter="250" />
                </v-col>
              </v-row>
            </v-form>
            <EmployeesManager 
              ref="employeeManager"
              :projectType="data.projectType"
              :tableLoading="tableLoading"
              :tableHeaders="tableHeaders"
              :tableItems="employees" 
              :emptyStateTitle="$t('wkt.admin.teams.team.empty-state.title')" 
              :emptyStateDescription="$t('wkt.admin.teams.team.empty-state.description')" 
              tableHeight="calc(100vh - 510px)"
              @remove-all-employees="confirmRemoveAllProjectEmployees($event)" 
              @add-employees="addProjectEmployees()" 
              @remove-employee="removeProjectEmployee($event)" />
            </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn v-if="showDeleteButton" color="#ff0000" @click="confirmDelete()">
						<v-icon left color="#ffffff">delete</v-icon>
						<span style="color: #ffffff;">{{ $t('wkt.admin.common.dialog-buttons.delete').toUpperCase() }}</span>
					</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="#185EA9" text @click="onSave()" style="font-size: 0.9em; font-weight: 400">
            {{ $t("wkt.admin.common.dialog-buttons.save").toUpperCase() }}
          </v-btn>
          <v-btn text @click="onCancel()" style="font-size: 0.9em; font-weight: 400">
            {{ $t("wkt.admin.common.dialog-buttons.cancel").toUpperCase()}}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

		<ColorDialog ref='dlgColor' />

    <TargetManagerDialog 
      ref="dlgTargetManager"
      :filterByProfile="true"
      :title="$t('wkt.admin.employee-selector-window.title_team')" 
      :subtitle="`${data.shortName} - ${data.description}`" 
      :targetLabel="$t('wkt.admin.employee-selector-window.target_placeholder')" />

		<ConfirmDialog ref='dlgConfirmRemoveAllProjectEmployees'
      :title="$t('wkt.admin.teams.team.remove-employees-confirm-dialog.title')"
      :message="confirmMessage"
      :titleOk="$t('wkt.admin.common.dialog-buttons.delete')"
      :titleCancel="$t('wkt.admin.common.dialog-buttons.cancel')"
      @confirmed="removeAllProjectEmployees()" />

    <ConfirmDialog ref='dlgConfirm'
      :title="$t('wkt.admin.teams.team.confirm-dialog.title')"
      :message="$t('wkt.admin.teams.team.confirm-dialog.text')"
      :titleOk="$t('wkt.admin.common.dialog-buttons.delete')"
      :titleCancel="$t('wkt.admin.common.dialog-buttons.cancel')"
      @confirmed="deleteItem()" />
  </v-row>
</template>

<script>
import BaseModule from "../../common/BaseModule.js";
import ColorDialog from "../../common/ColorDialog.vue";
import EmployeesManager from "../../common/EmployeesManager.vue";
import ConfirmDialog from "../../common/ConfirmDialog.vue";
import TargetManagerDialog from "./TargetManagerDialog.vue";
import api from "../../../api/worktime.js";

export default {
  name: "EditTeamDialog",
  mixins: [BaseModule],
  components: {
    ColorDialog, EmployeesManager, ConfirmDialog, TargetManagerDialog
  },
  data() {
    return {
      showDialog: false,
      loading: false,
      confirmMessage: '',
      data: {},
      employees: [],
      tableLoading: false,
      tableHeaders: [],
      showDeleteButton: false,
      dataHasChanged: false
    };
  },
  methods: {
    onWheel(event) {
      event.stopPropagation()
    },
    open(action, payload) {
      this.showDialog = true;
      this.dataHasChanged = false;
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
      
			this.tableHeaders = [
				{ text: this.$t('wkt.admin.projects.project.table.headers.name'), value: 'firstname', width: '25%', sortable: false },
				{ text: this.$t('wkt.admin.projects.project.table.headers.id_number'), value: 'idNumber', width: '15%', sortable: false },
				{ text: this.$t('wkt.admin.projects.project.table.headers.workplace'), value: 'workplaceName', width: '20%', sortable: false },
				{ text: this.$t('wkt.admin.projects.project.table.headers.department'), value: 'departmentName', width: '20%', sortable: false },
        { text: '', value: 'removeAction', width: '15%', sortable: false },
			];

      this.loading = true;

      this.showDeleteButton = this.data != null;
      api.getProjectById(payload,
        (res) => {
          this.data = res;
          this.loading = false;

          this.getProjectEmployeesByProjectId(this.data.id);
        },
        (error) => {
          this.loading = false;
          this.$emit("show-alert", { text: error.message, color: "error", timeout: 3000 });
        }
      );
    },
    openColorDialog() {
      this.$refs.dlgColor.open(this.data.color, (color) => {
        this.data.color = color;
      });
    },
    getProjectEmployeesByProjectId(projectId) {
      this.tableLoading = true;

      api.getFullProjectsEmployeesByProjectId(projectId,
        (res) => {
          this.tableLoading = false;
          this.employees = res;
        },
        (error) => {
          this.tableLoading = false;
          this.$emit("show-alert", { text: error.message, color: "error", timeout: 3000 });
        }
      );
    },
		confirmRemoveAllProjectEmployees(numEmployeesToRemove) {
      this.confirmMessage = this.$t('wkt.admin.teams.team.remove-employees-confirm-dialog.text');
      this.confirmMessage = this.confirmMessage.replace('%d', `${numEmployeesToRemove}`);
			
      this.$refs.dlgConfirmRemoveAllProjectEmployees.confirm(this.data);
		},
    removeAllProjectEmployees() {
      const data = {
        projectEmployeeIds: this.$refs.employeeManager.getIds(true)
      };

      this.tableLoading = true;

			api.deleteProjectEmployeesByProjectList(data,
        () => {
          this.tableLoading = false;
          this.getProjectEmployeesByProjectId(this.data.id);
          this.dataHasChanged = true;
        },
        (error) => {
          this.tableLoading = false;
          this.$emit("show-alert", { text: error.message, color: "error", timeout: 3000 });
        }
      );
		},
    addProjectEmployees() {
      this.$refs.dlgTargetManager.open(this.$refs.employeeManager.getEmployeeIds(false), this.data.id, (employeeIds) => {
        const alreadyAddedEmployeeIds = this.employees.map(e => e.employeeId);
        const selectedEmployeeIds = employeeIds.filter(id => !alreadyAddedEmployeeIds.includes(id))

        const data = {
          projectId: this.data.id,
          employeeIds: selectedEmployeeIds
        };

        this.tableLoading = true;

        api.addProjectEmployees(data,
          () => {
            this.tableLoading = false;
            this.getProjectEmployeesByProjectId(this.data.id);
            let message = this.$t('wkt.admin.projects.project.num_employees_added');
            message = message.replace('%d', `${selectedEmployeeIds.length}`);
            this.$emit("show-alert", { text: message, color: "success", timeout: 3000 });
            this.dataHasChanged = true;
          },
          (error) => {
            this.tableLoading = false;
            this.$emit("show-alert", { text: error.message, color: "error", timeout: 3000 });
          }
        );
      });
    },
    removeProjectEmployee(employeeId) {
      this.tableLoading = true;

      api.deleteProjectEmployeeById(employeeId,
        () => {
          this.tableLoading = false;
          this.getProjectEmployeesByProjectId(this.data.id);
          this.dataHasChanged = true;
        },
        (error) => {
          this.tableLoading = false;
          this.$emit("show-alert", { text: error.message, color: "error", timeout: 3000 });
        }
      );
    },
    onSave() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.loading = true;

      api.storeProject(this.data,
        (res) => {
          this.loading = false;
          if (res) {
            this.$emit("refresh");
            this.showDialog = false;
          } else {
            this.loading = false;
            this.$emit("show-alert", { text: res.message, color: "error", timeout: 3000 });
          }
        },
        (error) => {
          this.loading = false;
          this.$log.error(error);
          this.$emit("show-alert", { text: error.message, color: "error", timeout: 3000 });
        }
      );
    },
    confirmDelete() {
			this.$refs.dlgConfirm.confirm(this.data);
		},
    deleteItem() {
      this.loading = true;		

      api.deleteProjectById(this.data.id,
        () => {
          this.loading = false;
          this.$emit("refresh");
          this.showDialog = false;
        },
        (error) => {
          this.loading = false;
          this.$log.error(error);
          this.$emit("show-alert", { text: error.message, color: "error", timeout: 3000 });
        }
      );
    },
    onCancel() {
      if (this.dataHasChanged) {
        this.$emit('refresh');
        this.showDialog = false;
      } else {
        this.showDialog = false;
      }
    },
  }
}
</script>
